/**
 * ModelProvider
 * @module shared/components/ModalProvider
 * @author Olive
 */

import {
  PropsWithChildren,
  ReactNode,
  createContext,
  useContext,
  useState,
  useCallback,
} from 'react';
import logger from '@/shared/utils/Logger.ts';
import { Modal, ModalType } from '@/shared/components/Modal.tsx'; // <— 방금 보내주신 컴포넌트

type FunctionTypeWithUnknownArguments = (...args: unknown[]) => void;

/**
 * Context 기본 타입
 */
type ShowModal = (
  type: ModalType,
  content: ReactNode,
  onClose?: FunctionTypeWithUnknownArguments,
) => Promise<boolean>;

/**
 * 타입별 API를 정의한 ModalContext
 */
const ModalContext = createContext<
  ShowModal & {
    info(content: ReactNode, onClose?: FunctionTypeWithUnknownArguments): Promise<boolean>;
    warn(content: ReactNode, onClose?: FunctionTypeWithUnknownArguments): Promise<boolean>;
    error(content: ReactNode, onClose?: FunctionTypeWithUnknownArguments): Promise<boolean>;
    confirm(content: ReactNode, onClose?: FunctionTypeWithUnknownArguments): Promise<boolean>;
  }
>(undefined as never);

/**
 * ModalContext 접근 가능한 Modal Hook
 */
export const useModal = () => useContext(ModalContext);

/**
 * Modal 관리용 stack type 선언
 */
interface ModalEntry {
  id: number;
  type: ModalType;
  content: ReactNode;
  resolve: (value: boolean) => void;
  onClose?: FunctionTypeWithUnknownArguments;
}

/**
 * 전역으로 Modal을 관리하는 Provider를 반환합니다.
 * @param children - 전달받은 하위 컴포넌트
 * @returns ModalProvider
 * @author Olive
 */
export function ModalProvider({ children }: PropsWithChildren) {
  const [stack, setStack] = useState<ModalEntry[]>([]);

  /** 모달 표시 API — 전역에서 사용 */
  const showModal: ShowModal = useCallback(
    (type, content, onClose) =>
      new Promise<boolean>(resolve => {
        setStack(prev => [...prev, { id: Date.now(), type, content, resolve, onClose }]);
      }),
    [],
  );

  /** Modal 타입별 helper method 주입 */
  const modalApi = Object.assign(showModal, {
    info: (c: ReactNode, onClose?: FunctionTypeWithUnknownArguments) =>
      showModal('information', c, onClose),
    warn: (c: ReactNode, onClose?: FunctionTypeWithUnknownArguments) =>
      showModal('warning', c, onClose),
    error: (c: ReactNode, onClose?: FunctionTypeWithUnknownArguments) =>
      showModal('error', c, onClose),
    confirm: (c: ReactNode, onClose?: FunctionTypeWithUnknownArguments) =>
      showModal('confirm', c, onClose),
  } as const);

  /** 스택에서 꺼내며 Promise 해제 */
  const close = (id: number, accepted: boolean) =>
    setStack(prev => {
      const target = prev.find(m => m.id === id);
      target?.resolve(accepted);
      return prev.filter(m => m.id !== id);
    });

  return (
    <ModalContext.Provider value={modalApi}>
      {children}

      {stack.map(({ id, type, content, onClose: onClosedHandler }) => (
        <Modal
          key={id}
          type={type}
          open
          /* 바깥 클릭·ESC */
          onOpenChange={open => !open && close(id, false)}
          /* 확인/닫기 버튼 */
          onClose={(...modalArgs) => {
            try {
              if (typeof onClosedHandler === 'function') {
                onClosedHandler(...modalArgs);
              }
            } catch (e) {
              logger.error(e);
            }
            close(id, true);
          }}
        >
          {content}
        </Modal>
      ))}
    </ModalContext.Provider>
  );
}
